import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { usePermissions } from './PermissionContext';
import type { RoleCode } from '../types/domain';

/**
 * Dashboard PERSONA PREVIEW — UI-only. Lets a signed-in user browse the
 * premium dashboard layout built for each role family (Platform Owner,
 * Executive/Owner, Manager, HR, Finance, Warehouse) without that changing
 * anything about their REAL access. This context never calls
 * fn_check_permission()/fn_get_my_context() itself and never influences
 * AppShell's real nav visibility or any ProtectedRoute/RLS/RPC gate — those
 * all keep reading straight from PermissionContext, untouched. Switching
 * the preview only changes which <DashboardPage> template renders; for
 * roles/modules with no real backend wiring yet (Platform Owner cross-
 * tenant billing, HR, Finance) the template itself renders a persistent
 * DemoBanner and never claims the numbers shown are live.
 *
 * Defaults to the user's own real primary role so opening /app/dashboard
 * never silently substitutes demo data for someone's real one.
 */

interface DashboardPreviewState {
  previewRole: RoleCode;
  setPreviewRole: (role: RoleCode) => void;
  isRealRole: boolean;
  realRoles: RoleCode[];
}

export const DashboardPreviewContext = createContext<DashboardPreviewState | undefined>(undefined);

export function DashboardPreviewProvider({ children }: { children: ReactNode }) {
  const { context } = usePermissions();
  const realRoles = context && context.authenticated ? context.roles.map((r) => r.role_code) : [];
  const [previewRole, setPreviewRoleState] = useState<RoleCode>(realRoles[0] ?? 'TENANT_OWNER');
  // fn_get_my_context() resolves asynchronously, after this provider's very
  // first render — realRoles is still [] at mount time, so the useState
  // initializer above locks onto the 'TENANT_OWNER' fallback and (being a
  // useState initializer) never re-runs. Without this effect, a real
  // MANAGER/WAREHOUSE/PURCHASING/etc. user would render TENANT_OWNER's
  // Executive dashboard forever, since nothing ever corrects previewRole
  // once the real roles actually arrive. This snaps the workspace to the
  // user's real primary role the moment realRoles resolves — but only when
  // the user hasn't since made a deliberate preview choice themselves
  // (DEV preview, or picking among their own multiple real roles), so it
  // never clobbers an intentional selection.
  const userPickedPreview = useRef(false);
  useEffect(() => {
    if (!userPickedPreview.current && realRoles.length > 0 && !realRoles.includes(previewRole)) {
      setPreviewRoleState(realRoles[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [realRoles.join(',')]);

  const setPreviewRole = (role: RoleCode) => {
    userPickedPreview.current = true;
    setPreviewRoleState(role);
  };

  const value = useMemo<DashboardPreviewState>(
    () => ({ previewRole, setPreviewRole, isRealRole: realRoles.includes(previewRole), realRoles }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [previewRole, realRoles.join(',')]
  );

  return <DashboardPreviewContext.Provider value={value}>{children}</DashboardPreviewContext.Provider>;
}

export function useDashboardPreview(): DashboardPreviewState {
  const ctx = useContext(DashboardPreviewContext);
  if (!ctx) {
    throw new Error('useDashboardPreview() must be called within <DashboardPreviewProvider>');
  }
  return ctx;
}
